import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface User {
  id: string;
  username: string;
  displayName?: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  mockMode: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [mockMode, setMockMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSession = async () => {
    try {
      const [meRes, healthRes] = await Promise.all([
        fetch('/api/v1/auth/me', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)),
        fetch('/api/v1/system/health', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)),
      ]);
      if (meRes?.success) {
        setUser({
          id: String(meRes.data.id),
          username: meRes.data.username,
          displayName: meRes.data.displayName,
          role: meRes.data.role,
        });
      }
      if (healthRes?.success) setMockMode(!!healthRes.data.mockMode);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
  }, []);

  const login = async (username: string, password: string) => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error?.message || 'Login failed');
    }
    await loadSession();
  };

  const logout = async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    setMockMode(false);
  };

  return (
    <AuthContext.Provider value={{ user, mockMode, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
